import { SIMPLE_PROJECT_PHASES, type SimpleProjectPhaseState } from "../adapters/guided-project-steps.js";
import { humanIntakeStatus } from "../adapters/guided-project-steps.js";
import type { SitePackageId } from "@enrein/shared-contracts";
import type { StudioIntakeSummaryV1, StudioDepartureKitStatusV1 } from "@enrein/shared-contracts";
import type { PackageEligibilityBlocked } from "../../studio-shared/workflow/studio-workflow.js";
import { sitePackageDisplayLabel } from "../../studio-shared/site-package-labels.js";
import { GuidedButton, GuidedChip, GuidedFlowCopy } from "../primitives/GuidedPrimitives.js";
import { GuidedHandoffPanel } from "./GuidedHandoffPanel.js";

function formatList(values: readonly string[], emptyLabel: string): string {
  return values.length > 0 ? values.join(", ") : emptyLabel;
}

export function GuidedProjectRoomView({
  businessName,
  phaseState,
  intakeSummary,
  intakeStatusLabel,
  previewUrl,
  previewOpenAllowed,
  departureKit,
  handoffError,
  busy = false,
  validationIssues = [],
  packageEligibilityBlocked = null,
  onBuildWithPackage,
  onEnrichProfile,
  onPrimaryAction,
  onOpenPortal,
  onOpenPreview,
  onNavigate,
}: {
  readonly businessName: string;
  readonly phaseState: SimpleProjectPhaseState;
  readonly intakeSummary: StudioIntakeSummaryV1 | null;
  readonly intakeStatusLabel: string;
  readonly previewUrl: string | null;
  readonly previewOpenAllowed: boolean;
  readonly departureKit: StudioDepartureKitStatusV1 | null;
  readonly handoffError: string | null;
  readonly busy?: boolean;
  readonly validationIssues?: readonly string[];
  readonly packageEligibilityBlocked?: PackageEligibilityBlocked | null;
  readonly onBuildWithPackage?: (packageId: SitePackageId) => void;
  readonly onEnrichProfile?: () => void;
  readonly onPrimaryAction: (actionId: string) => void;
  readonly onOpenPortal: () => void;
  readonly onOpenPreview: () => void;
  readonly onNavigate: (view: string) => void;
}) {
  const primaryDisabled = busy || phaseState.primaryDisabled;

  return (
    <section className="enrein-guided-view is-active" id="view-project" aria-label="Project room">
      <div className="enrein-guided-identity-strip">
        <GuidedButton variant="subtle" onClick={() => onNavigate("home")}>
          ← Home
        </GuidedButton>
        <GuidedButton variant="subtle" onClick={() => onNavigate("projects")}>
          All projects
        </GuidedButton>
        <b>{businessName}</b>
        <GuidedChip>{intakeStatusLabel}</GuidedChip>
      </div>

      <ol className="enrein-guided-step-strip enrein-guided-phase-strip" aria-label="Project progress">
        {SIMPLE_PROJECT_PHASES.map((step) => (
          <li
            key={step.id}
            className={`enrein-guided-step enrein-guided-step--${phaseState.phaseStatus[step.id]}`}
            aria-current={phaseState.phaseStatus[step.id] === "current" ? "step" : undefined}
          >
            {step.label}
          </li>
        ))}
      </ol>

      {phaseState.showStaleApprovalBanner && phaseState.staleApprovalSummary ? (
        <div className="enrein-guided-stale-banner" role="status">
          <strong>Approval may be stale</strong>
          <p>{phaseState.staleApprovalSummary}</p>
        </div>
      ) : null}

      {intakeSummary ? (
        <div className="enrein-guided-intake-strip">
          <div className="enrein-guided-kicker">Client intake</div>
          <p>
            <strong>{intakeSummary.businessName}</strong> · {humanIntakeStatus(intakeSummary)}
          </p>
        </div>
      ) : null}

      {packageEligibilityBlocked ? (
        <div className="enrein-guided-facts-panel" aria-label="Package eligibility">
          <GuidedFlowCopy
            title={`${sitePackageDisplayLabel(packageEligibilityBlocked.requestedPackage)} not ready yet`}
            kicker="Package eligibility"
          >
            <p className="enrein-guided-muted">
              This profile does not meet the minimum requirements for the requested package.
            </p>
          </GuidedFlowCopy>
          <div className="enrein-guided-btn-row">
            {packageEligibilityBlocked.suggestedFallbackPackage && onBuildWithPackage ? (
              <GuidedButton
                variant="gold"
                disabled={busy}
                onClick={() => onBuildWithPackage(packageEligibilityBlocked.suggestedFallbackPackage!)}
              >
                Build {sitePackageDisplayLabel(packageEligibilityBlocked.suggestedFallbackPackage)}
              </GuidedButton>
            ) : null}
            {onEnrichProfile ? (
              <GuidedButton variant="line" disabled={busy} onClick={onEnrichProfile}>
                Enrich profile for {sitePackageDisplayLabel(packageEligibilityBlocked.requestedPackage)}
              </GuidedButton>
            ) : null}
          </div>
        </div>
      ) : null}

      {phaseState.showFactsPanel && phaseState.facts ? (
        <div className="enrein-guided-facts-panel" aria-label="Business facts review">
          <GuidedFlowCopy title="Facts to confirm" kicker="Review">
            <dl className="enrein-guided-facts-list">
              <div>
                <dt>Business name</dt>
                <dd>{phaseState.facts.businessName}</dd>
              </div>
              <div>
                <dt>Contact</dt>
                <dd>{phaseState.facts.contact}</dd>
              </div>
              <div>
                <dt>Services</dt>
                <dd>{formatList(phaseState.facts.services, "Not provided yet")}</dd>
              </div>
              <div>
                <dt>Service areas</dt>
                <dd>{formatList(phaseState.facts.serviceAreas, "Not provided yet")}</dd>
              </div>
              <div>
                <dt>Description</dt>
                <dd>{phaseState.facts.description}</dd>
              </div>
            </dl>
          </GuidedFlowCopy>
          {validationIssues.length > 0 ? (
            <ul className="enrein-guided-validation">
              {validationIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {(phaseState.phase === "preview_live" || phaseState.showHandoffPanel) && previewUrl ? (
        <div className="enrein-guided-preview-panel">
          <GuidedFlowCopy title="Preview link" kicker="Live preview">
            <p className="enrein-guided-preview-url">{previewUrl}</p>
          </GuidedFlowCopy>
        </div>
      ) : null}

      {phaseState.showHandoffPanel ? (
        <GuidedHandoffPanel
          departureKit={departureKit}
          exportRunId={phaseState.exportRunId}
          error={handoffError}
        />
      ) : null}

      <div className="enrein-guided-now-panel">
        <GuidedFlowCopy title={phaseState.nowTitle} kicker="Now">
          <p className="enrein-guided-muted">{phaseState.nowCopy}</p>
        </GuidedFlowCopy>
        <div className="enrein-guided-btn-row">
          <GuidedButton
            variant="gold"
            disabled={primaryDisabled}
            onClick={() => {
              if (phaseState.primaryActionId === "none") return;
              if (phaseState.primaryActionId === "open-portal") {
                onOpenPortal();
                return;
              }
              onPrimaryAction(phaseState.primaryActionId);
            }}
          >
            {busy && !phaseState.primaryDisabled ? "Working…" : phaseState.primaryCta}
          </GuidedButton>
          {(phaseState.phase === "preview_live" || phaseState.phase === "client_approved") && previewUrl ? (
            <GuidedButton variant="line" disabled={busy || !previewOpenAllowed} onClick={onOpenPreview}>
              Open preview
            </GuidedButton>
          ) : null}
          {phaseState.showSecondaryPortal ? (
            <GuidedButton variant="line" disabled={busy} onClick={onOpenPortal}>
              Open client portal
            </GuidedButton>
          ) : null}
        </div>
      </div>
    </section>
  );
}
